import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faArrowLeft, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import AdminLayout from './AdminLayout';
import adminService from '../../services/adminService';
import '../admin/AdminStyles.css';

function ProfessionalListingPricing() {
  const [pricingData, setPricingData] = useState({
    id: null,
    member_price: 7500,
    non_member_price: 10000,
    is_active: true
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  useEffect(() => {
    fetchPricingData();
  }, []);
  
  const fetchPricingData = async () => {
    setIsLoading(true);
    try {
      const data = await adminService.getProfessionalListingPrice();
      setPricingData(data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching pricing data:', error);
      setError('Failed to load pricing data. Please try again.');
      setIsLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPricingData({
      ...pricingData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveSuccess(false);
    setError(null);
    
    try {
      await adminService.updateProfessionalListingPrice({
        member_price: parseFloat(pricingData.member_price),
        non_member_price: parseFloat(pricingData.non_member_price),
        is_active: pricingData.is_active
      });
      
      setSaveSuccess(true);
      setIsSaving(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving pricing data:', error);
      setError('Failed to save changes. Please try again.');
      setIsSaving(false);
    }
  };
  
  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Professional Listing Pricing</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to="/admin/professional-listings">Listings</Link>
          </li>
          <li className="admin-breadcrumb-item active">Professional Listing Pricing</li>
        </ul>
      </div>
      
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Professional Listing Price Settings</h2>
          <Link to="/admin/professional-listings" className="admin-btn">
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Listings
          </Link>
        </div>
        
        <div className="admin-card-body">
          {isLoading ? (
            <div className="admin-loading">Loading pricing data...</div>
          ) : error ? (
            <div className="admin-error-message">{error}</div>
          ) : (
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="admin-info-box" style={{ marginBottom: '2rem' }}>
                <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '10px' }} />
                <span>This price will be charged to users who want to be listed in the professionals directory. Different rates apply for members and non-members.</span>
              </div>
              
              <div className="admin-form-group">
                <label htmlFor="member_price" className="admin-form-label">Member Price (₹)</label>
                <input
                  type="number"
                  id="member_price"
                  name="member_price"
                  value={pricingData.member_price}
                  onChange={handleChange}
                  className="admin-form-control"
                  min="0"
                  step="0.01"
                  required
                />
                <small className="admin-form-text">Fee charged to BVA members for professional listing.</small>
              </div>
              
              <div className="admin-form-group">
                <label htmlFor="non_member_price" className="admin-form-label">Non-Member Price (₹)</label>
                <input
                  type="number"
                  id="non_member_price"
                  name="non_member_price"
                  value={pricingData.non_member_price}
                  onChange={handleChange}
                  className="admin-form-control"
                  min="0"
                  step="0.01"
                  required
                />
                <small className="admin-form-text">Fee charged to non-members for professional listing.</small>
              </div>
              
              <div className="admin-form-group">
                <div className="admin-checkbox-container">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={pricingData.is_active}
                    onChange={handleChange}
                    className="admin-checkbox"
                  />
                  <label htmlFor="is_active" className="admin-checkbox-label">Active</label>
                </div>
                <small className="admin-form-text">When active, users can pay to list themselves in the professionals directory.</small>
              </div>
              
              {saveSuccess && (
                <div className="admin-success-message">
                  Professional listing prices updated successfully!
                </div>
              )}
              
              <div className="admin-form-actions">
                <button 
                  type="submit" 
                  className="admin-btn admin-btn-primary"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    'Saving...'
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} /> Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

export default ProfessionalListingPricing;
